"use client";

import { useEffect, useRef } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@heroui/button";
import {
  Calendar,
  ChevronRight,
  Dna,
  Ruler,
  UserIcon as Male,
  UserIcon as Female,
  Weight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Image } from "@heroui/image";

interface AnimalCardProps {
  animal: any;
  layout?: "grid" | "list";
  className?: string;
}

export default function AnimalCard({
  animal,
  layout = "grid",
  className = "",
}: AnimalCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set up hover animation using CSS transitions instead of
    // anime.js

    if (cardRef.current) {
      const imageEl = cardRef.current.querySelector(
        ".animal-image",
      ) as HTMLElement;
      const statsEl = cardRef.current.querySelector(
        ".animal-stats",
      ) as HTMLElement;

      if (imageEl) {
        imageEl.style.transition = "transform 0.3s ease";
      }

      if (statsEl) {
        statsEl.style.transition = "transform 0.3s ease, opacity 0.3s ease";
      }

      cardRef.current.addEventListener("mouseenter", () => {
        if (imageEl) {
          imageEl.style.transform = "scale(1.05)";
        }

        if (statsEl) {
          statsEl.style.transform = "translateY(-5px)";
          statsEl.style.opacity = "1";
        }
      });

      cardRef.current.addEventListener("mouseleave", () => {
        if (imageEl) {
          imageEl.style.transform = "scale(1)";
        }

        if (statsEl) {
          statsEl.style.transform = "translateY(0)";
          statsEl.style.opacity = "0.9";
        }
      });
    }
  }, []);

  if (layout === "list") {
    return (
      <Card
        ref={cardRef}
        className={`overflow-hidden transition-all hover:shadow-md ${className}`}
      >
        <div className="flex flex-col sm:flex-row">
          <div className="relative sm:top-5 lg:top-0 lg:w-[500px] sm:w-[400px] h-48">
            <Image
              isBlurred
              isZoomed
              loading={"eager"}
              src={
                (animal.imageUrls && animal.imageUrls[0]) || "/placeholder.svg"
              }
              alt={animal.name}
              // className="object-cover animal-image transition-all w-full
              // h-full absolute top-0 left-0"
            />
            <div className="absolute top-2 left-2 z-50">
              <Badge variant="secondary" className="font-semibold">
                {animal.dgr}
              </Badge>
            </div>
            <div className="absolute top-2 right-2 z-50">
              {animal.bullType === "Beef Bull" ? (
                <Badge className="bg-blue-500 hover:bg-blue-600">
                  <Male className="h-3 w-3 mr-1" /> Beef Bull
                </Badge>
              ) : (
                <Badge className="bg-pink-500 hover:bg-pink-600">
                  <Female className="h-3 w-3 mr-1" /> Dairy Bull
                </Badge>
              )}
            </div>
          </div>

          <div className="p-4 flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold">{animal.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {animal.racialComposition} • {animal.breedingType}
                </p>
              </div>
              <Badge
                variant={
                  animal.status === "Healthy" ? "outline" : "destructive"
                }
                className={
                  animal.status === "Healthy"
                    ? "border-green-500 text-green-600"
                    : ""
                }
              >
                {animal.status}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {new Date(animal.birthDate).toLocaleDateString()} (
                  {animal.age} mon)
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Weight className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{animal.weight} kg</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Ruler className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{animal.size}</span>
              </div>
              {/* <div className="flex items-center gap-1.5"> */}
              {/*   <Award className="h-4 w-4 text-muted-foreground" /> */}
              {/*   <span className="text-sm">{animal.evaluation}</span> */}
              {/* </div> */}
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Farm:</span>{" "}
                {animal.farm}
              </div>
              <div>
                <span className="text-muted-foreground">Class:</span>{" "}
                {animal.cattleClass}
              </div>
              <div>
                <span className="text-muted-foreground">ADG:</span> {animal.adg}{" "}
                kg/day
              </div>
              <div>
                <span className="text-muted-foreground">Dam:</span>{" "}
                {animal.damName}
              </div>
              <div>
                <span className="text-muted-foreground">Sire:</span>{" "}
                {animal.sireName}
              </div>
              <div>
                <span className="text-muted-foreground">Offspring:</span>{" "}
                {animal.numberOffspring}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Link to={`/Browse-Animal/${animal._id}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 bg-[#008c8fff]"
                >
                  View Profile <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      ref={cardRef}
      className={`overflow-hidden  transition-all hover:shadow-md ${className}`}
    >
      <div className="relative  h-48">
        <Image
          isBlurred
          isZoomed
          loading={"eager"}
          src={(animal.imageUrls && animal.imageUrls[0]) || "/placeholder.svg"}
          alt={animal.name}
          height={192}
          width={"100%"}
          // className="object-cover animal-image transition-all w-full h-full
          // absolute top-0 left-0"
        />
        <div className="absolute top-2 left-2 z-50">
          <Badge variant="secondary" className="font-semibold">
            {animal.dgr}
          </Badge>
        </div>
        <div className="absolute top-2 right-2 z-50">
          {animal.bullType === "Beef Bull" ? (
            <Badge className="bg-blue-500 hover:bg-blue-600">
              <Male className="h-3 w-3 mr-1" /> Beef Bull
            </Badge>
          ) : (
            <Badge className="bg-pink-500 hover:bg-pink-600">
              <Female className="h-3 w-3 mr-1" /> Dairy Bull
            </Badge>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 animal-stats opacity-90">
          <div className="flex justify-between text-white">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span className="text-xs">{animal.age}y</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Weight className="h-4 w-4" />
              <span className="text-xs">{animal.weight} kg</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Dna className="h-4 w-4" />
              <span className="text-xs">{animal.racialComposition}</span>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold">{animal.name}</h3>
            <p className="text-sm text-muted-foreground">
              {animal.breedingType}
            </p>
          </div>
          <Badge
            variant={animal.status === "Healthy" ? "outline" : "destructive"}
            className={
              animal.status === "Healthy"
                ? "border-green-500 text-green-600"
                : ""
            }
          >
            {animal.status}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
          <div>
            <span className="text-muted-foreground">Farm:</span> {animal.farm}
          </div>
          <div>
            <span className="text-muted-foreground">Class:</span>{" "}
            {animal.cattleClass}
          </div>
          <div>
            <span className="text-muted-foreground">Dam:</span> {animal.damName}
          </div>
          <div>
            <span className="text-muted-foreground">Sire:</span>{" "}
            {animal.sireName}
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex justify-between items-center">
        {/* <div className="flex items-center"> */}
        {/*   <Award */}
        {/*     className={`h-4 w-4 mr-1 ${ */}
        {/*       animal.evaluation === "Superior" */}
        {/*         ? "text-amber-500" */}
        {/*         : animal.evaluation === "Excellent" */}
        {/*           ? "text-emerald-500" */}
        {/*           : animal.evaluation === "Very Good" */}
        {/*             ? "text-blue-500" */}
        {/*             : "text-slate-500" */}
        {/*     }`} */}
        {/*   /> */}
        {/*   <span className="text-sm font-medium">{animal.evaluation}</span> */}
        {/* </div> */}
        {/* <Link to={`/Browse-Animal/${animal._id}`}> */}
        <div className="mt-4 flex justify-end">
          <Link to={`/Browse-Animal/${animal._id}`}>
            <Button
              size="sm"
              className="gap-1 bg-[#008c8fff] text-gray-100"
              color={"primary"}
            >
              View Profile <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
}
